import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Clock, MessageCircle, Send, Loader2, Navigation, CheckCircle2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT } from "@/constants/testIds";
import {
  BUSINESS,
  telHref,
  whatsappHref,
  directionsHref,
  mapsEmbedSrc,
} from "@/data/site";
import { fadeUp, stagger, viewport } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildContactWaUrl = ({ name, mobile, email, message }) => {
  const lines = [
    "Hello Paliwal Medicos, I have a message from your website.",
    "",
    `Name: ${name}`,
    `Mobile: ${mobile}`,
  ];
  if (email) lines.push(`Email: ${email}`);
  lines.push("", "Message:", message);
  return `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null); // { waUrl }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your name.");
    if (form.mobile.trim().length < 6) return toast.error("Please enter a valid mobile number.");
    if (form.message.trim().length < 2) return toast.error("Please enter a message.");

    const clean = {
      name: form.name.trim(),
      mobile: form.mobile.trim(),
      email: form.email.trim(),
      message: form.message.trim(),
    };

    const payload = {
      name: clean.name,
      mobile: clean.mobile,
      message: clean.message,
    };
    if (clean.email) payload.email = clean.email;

    try {
      setLoading(true);
      await axios.post(`${API}/contact`, payload);
      const waUrl = buildContactWaUrl(clean);
      const opened = window.open(waUrl, "_blank", "noopener,noreferrer");
      if (opened) {
        toast.success("Message saved — opening WhatsApp to send it to the pharmacy.");
      } else {
        toast.success("Message saved. Tap the WhatsApp button to send it to the pharmacy.");
      }
      setSent({ waUrl });
      setForm({ name: "", mobile: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl">
          <motion.p
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            variants={fadeUp}
            className="font-serif-accent italic text-lg text-brand-green"
          >
            Visit or reach us
          </motion.p>
          <motion.h2
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            variants={fadeUp}
            className="mt-3 font-heading font-bold tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl leading-tight"
          >
            We're here for your health.
          </motion.h2>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          {/* Left: info + map */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            variants={stagger}
            className="flex flex-col gap-6"
          >
            <motion.div
              variants={fadeUp}
              className="rounded-3xl border border-slate-100 bg-surface p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            >
              <h3 className="font-heading text-xl font-bold text-ink">
                {BUSINESS.name}
              </h3>
              <ul className="mt-6 space-y-4 text-slate-600">
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
                  <span>{BUSINESS.addressLines.join(", ")}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-brand-blue shrink-0" />
                  <a href={telHref} className="hover:text-brand-blue font-medium">
                    {BUSINESS.phoneDisplay}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-brand-blue shrink-0" />
                  <span>
                    {BUSINESS.hoursNote} · {BUSINESS.hours}
                  </span>
                </li>
              </ul>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-green px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-brand-green-dark active:scale-95"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
                <a
                  data-testid={CONTACT.directionsBtn}
                  href={directionsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-ink transition-all hover:border-brand-blue hover:text-brand-blue active:scale-95"
                >
                  <Navigation className="h-4 w-4" /> Get Directions
                </a>
              </div>
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="overflow-hidden rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-h-[280px] flex-1"
            >
              <iframe
                title="Paliwal Medicos location"
                src={mapsEmbedSrc}
                className="h-full w-full min-h-[280px]"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </motion.div>
          </motion.div>

          {/* Right: form */}
          <motion.form
            onSubmit={submit}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            variants={fadeUp}
            className="rounded-3xl border border-slate-100 bg-surface p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          >
            <h3 className="font-heading text-xl font-bold text-ink">
              Send us a message
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              After submitting, WhatsApp opens with your message ready to send to Paliwal Medicos ({BUSINESS.phoneDisplay}).
            </p>

            {sent && (
              <div
                data-testid="contact-success-card"
                className="mt-5 rounded-2xl border border-brand-green/30 bg-brand-green/5 p-5"
              >
                <div className="flex items-start gap-3">
                  <span className="grid place-items-center h-10 w-10 rounded-full bg-brand-green text-white shrink-0">
                    <CheckCircle2 className="h-5 w-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-semibold text-ink">
                      Message ready
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                      Tap the button below to send your message to the pharmacy on WhatsApp.
                    </p>
                    <a
                      data-testid="contact-send-whatsapp-btn"
                      href={sent.waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-green px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-green/25 transition-all hover:bg-brand-green-dark active:scale-95"
                    >
                      <MessageCircle className="h-4 w-4" /> Send on WhatsApp
                    </a>
                  </div>
                  <button
                    onClick={() => setSent(null)}
                    className="grid place-items-center h-8 w-8 rounded-full text-slate-400 hover:bg-slate-100 hover:text-ink transition-colors"
                    aria-label="Dismiss"
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            <div className="mt-6 grid gap-4">
              <Input
                data-testid={CONTACT.nameInput}
                value={form.name}
                onChange={set("name")}
                placeholder="Name"
                className="h-12 rounded-xl border-slate-200 bg-white focus-visible:ring-2 focus-visible:ring-brand-blue/25 focus-visible:border-brand-blue"
              />
              <Input
                data-testid={CONTACT.mobileInput}
                value={form.mobile}
                onChange={set("mobile")}
                inputMode="tel"
                placeholder="Mobile number"
                className="h-12 rounded-xl border-slate-200 bg-white focus-visible:ring-2 focus-visible:ring-brand-blue/25 focus-visible:border-brand-blue"
              />
              <Input
                data-testid={CONTACT.emailInput}
                value={form.email}
                onChange={set("email")}
                type="email"
                placeholder="Email (optional)"
                className="h-12 rounded-xl border-slate-200 bg-white focus-visible:ring-2 focus-visible:ring-brand-blue/25 focus-visible:border-brand-blue"
              />
              <Textarea
                data-testid={CONTACT.messageInput}
                value={form.message}
                onChange={set("message")}
                rows={4}
                placeholder="How can we help you?"
                className="rounded-xl border-slate-200 bg-white focus-visible:ring-2 focus-visible:ring-brand-blue/25 focus-visible:border-brand-blue"
              />
              <button
                data-testid={CONTACT.submitBtn}
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-brand-blue/25 transition-all hover:bg-brand-blue-dark active:scale-95 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" /> Submit
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
